<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength, integer, between } from '@vuelidate/validators';

import Input from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Switch from 'next/switch/Switch.vue';
import Button from 'next/button/Button.vue';

const props = defineProps({
  server: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('wgptMcpServers/getUIFlags');

// The upper bound comes from the deployment's request timeout, so the server reports it.
const minTimeout = computed(() => props.server.min_timeout_seconds ?? 5);
const maxTimeout = computed(() => props.server.max_timeout_seconds ?? 15);

const state = reactive({
  name: '',
  description: '',
  instructions: '',
  timeout_seconds: null,
  active: true,
});

watch(
  () => props.server,
  server => {
    state.name = server.name || '';
    state.description = server.description || '';
    state.instructions = server.instructions || '';
    state.timeout_seconds = server.timeout_seconds ?? maxTimeout.value;
    state.active = server.active ?? true;
  },
  { immediate: true }
);

const rules = computed(() => ({
  name: { required, maxLength: maxLength(255) },
  timeout_seconds: {
    required,
    integer,
    between: between(minTimeout.value, maxTimeout.value),
  },
}));

const v$ = useVuelidate(rules, state);

const hasPendingChanges = computed(
  () =>
    state.name !== (props.server.name || '') ||
    state.description !== (props.server.description || '') ||
    state.instructions !== (props.server.instructions || '') ||
    Number(state.timeout_seconds) !== props.server.timeout_seconds ||
    state.active !== props.server.active
);

const nameError = computed(() => {
  if (!v$.value.name.$error) return '';
  if (v$.value.name.maxLength.$invalid) {
    return t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.NAME.ERROR_MAX_LENGTH');
  }
  return t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.NAME.ERROR');
});

const timeoutError = computed(() =>
  v$.value.timeout_seconds.$error
    ? t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.TIMEOUT.ERROR', {
        min: minTimeout.value,
        max: maxTimeout.value,
      })
    : ''
);

const handleSave = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    await store.dispatch('wgptMcpServers/update', {
      id: props.server.id,
      name: state.name,
      description: state.description,
      instructions: state.instructions,
      timeout_seconds: Number(state.timeout_seconds),
      active: state.active,
    });
    useAlert(t('WGPT_MCP_SERVERS.EDIT.SETTINGS.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.message || t('WGPT_MCP_SERVERS.EDIT.SETTINGS.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="mx-6 min-h-full">
    <div class="py-4">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('WGPT_MCP_SERVERS.EDIT.SETTINGS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ t('WGPT_MCP_SERVERS.EDIT.SETTINGS.DESCRIPTION') }}
      </p>
    </div>

    <form class="flex flex-col gap-6 max-w-2xl" @submit.prevent="handleSave">
      <Input
        v-model="state.name"
        :label="t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.NAME.LABEL')"
        :message="nameError"
        :message-type="nameError ? 'error' : 'info'"
        @input="v$.name.$touch"
      />

      <TextArea
        v-model="state.description"
        custom-text-area-class="!outline !outline-0"
        :label="t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.DESCRIPTION.LABEL')"
        rows="3"
      />

      <TextArea
        v-model="state.instructions"
        custom-text-area-class="!outline !outline-0"
        :label="t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.INSTRUCTIONS.LABEL')"
        :message="t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.INSTRUCTIONS.HELP')"
        message-type="info"
        rows="5"
      />

      <Input
        v-model="state.timeout_seconds"
        type="number"
        :min="String(minTimeout)"
        :max="String(maxTimeout)"
        step="1"
        class="max-w-xs"
        :label="t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.TIMEOUT.LABEL')"
        :message="
          timeoutError ||
          t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.TIMEOUT.HELP', {
            max: maxTimeout,
          })
        "
        :message-type="timeoutError ? 'error' : 'info'"
        @input="v$.timeout_seconds.$touch"
      />

      <label class="flex items-center gap-3 cursor-pointer">
        <Switch v-model="state.active" />
        <span class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.ACTIVE.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.ACTIVE.HELP') }}
          </span>
        </span>
      </label>

      <div>
        <Button
          type="submit"
          :label="t('WGPT_MCP_SERVERS.EDIT.SETTINGS.FORM.SAVE')"
          :is-loading="uiFlags.isUpdating"
          :disabled="!hasPendingChanges || uiFlags.isUpdating"
          size="sm"
        />
      </div>
    </form>
  </div>
</template>
